import './App.css';
import "bootstrap/dist/css/bootstrap.min.css";
import { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
// import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import { NotFound, PlaceholderPage } from './components/Notfound';
import ErrorBoundary from './components/ErrorBoundary';
import { useTranslation } from './i18n/i18n';

import Dashboard from './pages/Dashboard';
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import UpdatePassword from './pages/Auth/UpdatePassword';

import SettingsPage from './pages/Settings/Settings';
import SecuritySettingsPage from './pages/Settings/SecuritySettingsPage';
import ProfileSettingsPage from './pages/Settings/ProfileSettingsPage';
import SessionsPage from './pages/Settings/SessionsPage';

import UsersPage from './pages/Users/Users';
import UserDetails from './pages/Users/UserDetails';

import CustomersPage from './pages/Customers/Customers';
import CustomerDetails from './pages/Customers/CustomerDetails';

import SuppliersPage from './pages/Supplier/Supplier';
import SupplierDetails from './pages/Supplier/SupplierDetails';

import BookingsPage from './pages/Booking/Booking';
import BookingDetailPage from './pages/Booking/BookingDetails';
import VoucherPage from './pages/Booking/voucher/VoucherPage';
import VoucherEditPage from './pages/Booking/voucher/EditVoucher/VoucherEditPage';

import ProductsPage from './pages/Product/Product';
import ProductDetails from './pages/Product/ProductDetails';

import ExchangeRatePage from './pages/Exchange/ExchangeRatePage';

import SalesStatusPage from './pages/SalesStatus/SalesStatusPage';

import InvoicesPage from './pages/Invoices/Invoices';
import InvoiceDetailsCustomer from './pages/Invoices/InvoiceDetailsCustomer';
import InvoiceDetailsProvider from './pages/Invoices/InvoiceDetailsProvider';

import StatisticsPage from './pages/Stats/StatisticsPage';

import SupportAdminPage from './pages/Support/SupportAdminPage';
import SupportSupplierPage from './pages/Support/SupportSupplierPage';

// Composant Layout pour les pages authentifiées
const AuthenticatedLayout = ({ children, lang, onLangChange, onLogout, userData }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Détection de la taille de l'écran pour mobile
  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarCollapsed(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleSidebarCollapse = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  const handleMenuClick = useCallback(() => {
    if (isMobile) {
      setSidebarOpen(prev => !prev);
    } else {
      setSidebarCollapsed(prev => !prev);
    }
  }, [isMobile]);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f9fafb',
    }}>
      <Sidebar
        currentPage={location.pathname}
        isOpen={sidebarOpen}
        onClose={closeSidebar}
        lang={lang}
        isMobile={isMobile}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapse}
        onLogout={onLogout}
        userData={userData}
      />
      <div style={{
        marginLeft: !isMobile ? (sidebarCollapsed ? '80px' : '250px') : '0',
        transition: 'margin-left 0.3s ease',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Topbar
          onMenuClick={handleMenuClick}
          lang={lang}
          onLangChange={onLangChange}
          onLogout={onLogout}
          userData={userData}
        />
        <main style={{ flex: 1 }}>
          {children}
        </main>
      </div>
    </div>
  );
};

// Route protégée
const ProtectedRoute = ({ children, isAuthenticated }) => {
  const location = useLocation();

  if (!isAuthenticated) {
    sessionStorage.setItem(
      "redirectAfterLogin",
      location.pathname
    );

    return <Navigate to="/login" replace />;
  }

  return children;
};


// Composant principal de l'application avec routing
const AppContent = () => {
  const navigate = useNavigate()
  const [lang, setLang] = useState('en');
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    return !!token;
  });
  const [userData, setUserData] = useState(() => {
    const savedUser = localStorage.getItem('userData') || sessionStorage.getItem('userData');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const { t } = useTranslation(lang);

const handleLogin = useCallback((user) => {
  setIsAuthenticated(true);
  setUserData(user);

  if (user.rememberMe) {
    localStorage.setItem("authToken", user.token);
    localStorage.setItem("userData", JSON.stringify(user));
  } else {
    sessionStorage.setItem("authToken", user.token);
    sessionStorage.setItem("userData", JSON.stringify(user));
  }

  const redirectPath =
    sessionStorage.getItem("redirectAfterLogin") || "/dashboard";

  sessionStorage.removeItem("redirectAfterLogin");

  navigate(redirectPath);
}, []);


  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setUserData(null);
    
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('userData');
  }, []);

  const handleLangChange = useCallback((newLang) => {
    setLang(newLang);
  }, []);

  return (
<Routes>
  {/* Routes publiques */}
  <Route path="/login" element={
    <Login onLogin={handleLogin} lang={lang} onLangChange={handleLangChange} />
  } />
  <Route path="/login/forgot-password" element={
    <ForgotPassword lang={lang} onLangChange={handleLangChange} />
  } />
  <Route path="/login/update-password" element={
    <UpdatePassword lang={lang} onLangChange={handleLangChange} />
  } />

  {/* Routes protégées - Main */}
  <Route path="/" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <Dashboard lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  <Route path="/dashboard" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <Dashboard lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  {/* Routes protégées - Management */}

  <Route path="/users" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <UsersPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

      <Route path="/users/:id" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <UserDetails lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

    <Route path="/customers" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <CustomersPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

      <Route path="/customers/:id" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <CustomerDetails lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  <Route path="/suppliers" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <SuppliersPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

    <Route path="/suppliers/:id" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <SupplierDetails lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  <Route path="/products" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <ProductsPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

      <Route path="/products/:id" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <ProductDetails lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />


  <Route path="/promotions" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <PlaceholderPage title={t('promotions')} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  <Route path="/bookings" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <BookingsPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  <Route path="/bookings/:id" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <BookingDetailPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

    <Route path="/voucher/:id" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <VoucherPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

      <Route path="/voucher/edit/:id" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <VoucherEditPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  {/* Routes protégées - Finance */}

  <Route path="/exchange" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <ExchangeRatePage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  <Route path="/sales" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <SalesStatusPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  <Route path="/invoices" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <InvoicesPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

        <Route path="/invoices/customers/:id" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <InvoiceDetailsCustomer lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />


        <Route path="/invoices/providers/:id" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <InvoiceDetailsProvider lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  <Route path="/statistics" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <StatisticsPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  {/* Routes protégées - Support */}
  <Route path="/admin-support" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <SupportAdminPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  <Route path="/supplier-support" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <SupportSupplierPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

  {/* Routes protégées - Settings */}
  <Route path="/settings" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <SettingsPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

    <Route path="/settings/security" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <SecuritySettingsPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />

      <Route path="/settings/profile" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <ProfileSettingsPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />


    <Route path="/settings/sessions" element={
    <ProtectedRoute isAuthenticated={isAuthenticated}>
      <AuthenticatedLayout 
        lang={lang} 
        onLangChange={handleLangChange}
        onLogout={handleLogout}
        userData={userData}
      >
        <SessionsPage lang={lang} />
      </AuthenticatedLayout>
    </ProtectedRoute>
  } />


  {/* Redirection par défaut */}
  <Route path="*" element={<NotFound />} />
</Routes>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <Router>
        <AppContent />
      </Router>
    </ErrorBoundary>
  );
};

export default App;